import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import { HTTPCode } from "src/contexts/_shared/domain/valueObjects/HTTPCodes";

// Dev-only (STATIC_DEPS). Makes repository methods fail to reproduce error screens, e.g.
// ?fail=sendTwoFactor:404:ACTOR_REMOVED,getConsents:500 (format: method[:status[:code]])
const FAIL_PARAM = "fail";
const DEFAULT_STATUS = 404;

type Failure = { status: number; code?: string };

const getFailures = (): Record<string, Failure> => {
  const raw = new URLSearchParams(window.location.search).get(FAIL_PARAM);
  if (!raw) {
    return {};
  }
  return Object.fromEntries(
    raw.split(",").map((entry) => {
      const [method, status, code] = entry.split(":");
      return [method, { status: Number(status) || DEFAULT_STATUS, code }];
    }),
  );
};

export const withFailureInjection = <T extends object>(repository: T): T =>
  new Proxy(repository, {
    get(target, property, receiver) {
      const value = Reflect.get(target, property, receiver);
      if (typeof value !== "function" || typeof property !== "string") {
        return value;
      }
      return (...args: unknown[]) => {
        const failure = getFailures()[property];
        if (failure) {
          return Promise.reject(
            new HTTPError(
              `Injected failure for ${property}`,
              new HTTPCode(failure.status),
              failure.code,
            ),
          );
        }
        return value.apply(target, args);
      };
    },
  });
